import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { isDeliveryFeeLine } from './demOrderBatches';
import type {
  DemBlanketAttachmentRef,
  DemBlanketOrder,
  DemBlanketOrderLine,
  DemBlanketOrderLineInput,
  DemBlanketOrderMovement,
  DemBlanketOrderStatus,
} from '../types/dem';

const BLANKET_ATTACHMENT_BUCKET = 'dem-blanket-attachments';

const BLANKET_HEADER_SELECT =
  'id, customer_code, po_number, customer_name, status, notes, created_by, created_at, updated_at, po_attachments, lines:blanket_order_lines(id, blanket_id, item_code, description, ordered_qty, uom, unit_price)';

function demDb() {
  return getDbSupabase('dem');
}

export function normalizeBlanketPoNumber(raw: string): string {
  return raw.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
}

function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

type LineRow = {
  id: string;
  blanket_id: string;
  item_code: string;
  description: string;
  ordered_qty: number | string;
  uom: string;
  unit_price: number | string | null;
};

type MovementRow = {
  id: string;
  blanket_line_id: string;
  dem_order_id: string;
  dem_batch_id: string;
  qty: number | string;
  source: string;
  created_by: string;
  created_at: string;
};

type HeaderRow = {
  id: string;
  customer_code: string;
  po_number: string;
  customer_name: string;
  status: string;
  notes: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  po_attachments?: unknown;
};

function parsePoAttachments(raw: unknown): DemBlanketAttachmentRef[] {
  if (!Array.isArray(raw)) return [];
  const out: DemBlanketAttachmentRef[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const rec = row as Record<string, unknown>;
    const path = String(rec.path ?? '').trim();
    const fileName = String(rec.fileName ?? '').trim();
    if (!path || !fileName) continue;
    out.push({
      path,
      fileName,
      size: typeof rec.size === 'number' ? rec.size : undefined,
      mime: typeof rec.mime === 'string' ? rec.mime : undefined,
    });
  }
  return out;
}

function buildLinesWithTotals(
  lines: LineRow[],
  movements: MovementRow[],
): DemBlanketOrderLine[] {
  const deliveredByLine = new Map<string, number>();
  for (const move of movements) {
    const id = move.blanket_line_id;
    deliveredByLine.set(id, (deliveredByLine.get(id) ?? 0) + num(move.qty));
  }

  return lines.map((line) => {
    const ordered = num(line.ordered_qty);
    const delivered = deliveredByLine.get(line.id) ?? 0;
    return {
      id: line.id,
      blanket_id: line.blanket_id,
      item_code: String(line.item_code ?? ''),
      description: String(line.description ?? ''),
      ordered_qty: ordered,
      uom: String(line.uom ?? 'PCS'),
      unit_price: line.unit_price == null || line.unit_price === '' ? null : num(line.unit_price),
      delivered_qty: delivered,
      // Never expose negative outstanding — over-delivery is blocked at submit/export.
      outstanding_qty: Math.max(0, ordered - delivered),
    };
  });
}

function parseBlanketStatus(raw: string): DemBlanketOrderStatus {
  if (raw === 'Closed') return 'Closed';
  if (raw === 'Completed') return 'Completed';
  return 'Open';
}

/** All lines fully delivered (outstanding is 0). */
export function isBlanketFullyDelivered(lines: DemBlanketOrderLine[] | undefined): boolean {
  const rows = lines ?? [];
  if (rows.length === 0) return false;
  return rows.every((line) => line.outstanding_qty <= 0);
}

/**
 * When a DEM order PO matches an Open/Completed blanket:
 * - every ordered item must exist on that blanket
 * - qty must not exceed remaining outstanding (minus other Pending orders)
 * No matching blanket → no-op. Throws a user-facing Error to block submit.
 */
export async function assertOrderWithinBlanketOutstanding(input: {
  poNumber: string;
  lines: Array<{ item_code: string; qty: number; description?: string }>;
  excludeOrderId?: string;
}): Promise<void> {
  const po = normalizeBlanketPoNumber(input.poNumber);
  if (!po) return;

  const requested = new Map<string, number>();
  for (const line of input.lines) {
    const code = String(line.item_code ?? '')
      .trim()
      .toUpperCase();
    const description = String(line.description ?? '');
    if (!code || isDeliveryFeeLine(code, description)) continue;
    const qty = num(line.qty);
    if (qty <= 0) continue;
    requested.set(code, (requested.get(code) ?? 0) + qty);
  }
  if (requested.size === 0) return;

  const db = demDb();
  const { data, error } = await db
    .from('blanket_orders')
    .select(
      'id, po_number, status, lines:blanket_order_lines(id, blanket_id, item_code, description, ordered_qty, uom, unit_price)',
    )
    .eq('po_number', po)
    .in('status', ['Open', 'Completed'])
    .maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) return;

  const header = data as HeaderRow & { lines?: LineRow[] | null };
  const lineIds = (header.lines ?? []).map((l) => l.id);
  let movements: MovementRow[] = [];
  if (lineIds.length > 0) {
    const { data: moves, error: moveError } = await db
      .from('blanket_order_movements')
      .select('id, blanket_line_id, dem_order_id, dem_batch_id, qty, source, created_by, created_at')
      .in('blanket_line_id', lineIds);
    if (moveError) throw new Error(formatSupabaseError(moveError));
    movements = (moves ?? []) as MovementRow[];
  }

  const blanketLines = buildLinesWithTotals(header.lines ?? [], movements);
  const outstandingByCode = new Map<string, { outstanding: number; description: string }>();
  for (const line of blanketLines) {
    const code = line.item_code.trim().toUpperCase();
    if (!code) continue;
    outstandingByCode.set(code, {
      outstanding: Math.max(0, line.outstanding_qty),
      description: line.description,
    });
  }

  // Other Pending orders on the same PO already reserve outstanding until export.
  const reservedByCode = new Map<string, number>();
  const { data: pendingRows, error: pendingError } = await db
    .from('orders')
    .select('order_id, ref_no, items:order_items(item_code, description, qty)')
    .eq('status', 'Pending');
  if (pendingError) throw new Error(formatSupabaseError(pendingError));

  const excludeId = input.excludeOrderId?.trim() ?? '';
  for (const order of (pendingRows ?? []) as Array<{
    order_id?: string;
    ref_no?: string;
    items?: Array<{ item_code?: string; description?: string; qty?: number | string }> | null;
  }>) {
    if (excludeId && String(order.order_id ?? '') === excludeId) continue;
    if (normalizeBlanketPoNumber(String(order.ref_no ?? '')) !== po) continue;
    for (const item of order.items ?? []) {
      const code = String(item.item_code ?? '')
        .trim()
        .toUpperCase();
      const description = String(item.description ?? '');
      if (!code || isDeliveryFeeLine(code, description)) continue;
      if (!outstandingByCode.has(code)) continue;
      const qty = num(item.qty);
      if (qty <= 0) continue;
      reservedByCode.set(code, (reservedByCode.get(code) ?? 0) + qty);
    }
  }

  const problems: string[] = [];
  for (const [code, qty] of requested) {
    const blanketLine = outstandingByCode.get(code);
    if (!blanketLine) {
      problems.push(`${code}: not on this blanket PO`);
      continue;
    }
    const reserved = reservedByCode.get(code) ?? 0;
    const available = Math.max(0, blanketLine.outstanding - reserved);
    if (available <= 0) {
      problems.push(
        `${code}: no outstanding left` +
          (reserved > 0 ? ` (0 after ${formatQtyPlain(reserved)} already pending)` : ''),
      );
      continue;
    }
    if (qty > available + 1e-9) {
      problems.push(
        `${code}: ordered ${formatQtyPlain(qty)}, outstanding left ${formatQtyPlain(available)}` +
          (reserved > 0 ? ` (${formatQtyPlain(reserved)} already pending)` : ''),
      );
    }
  }

  if (problems.length === 0) return;
  throw new Error(`Cannot submit against blanket PO ${po}:\n• ${problems.join('\n• ')}`);
}

function formatQtyPlain(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

function headerWithTotals(
  header: HeaderRow,
  lines: DemBlanketOrderLine[],
): DemBlanketOrder {
  const ordered = lines.reduce((sum, line) => sum + line.ordered_qty, 0);
  const delivered = lines.reduce((sum, line) => sum + line.delivered_qty, 0);
  return {
    id: header.id,
    customer_code: header.customer_code,
    po_number: header.po_number,
    customer_name: header.customer_name,
    status: parseBlanketStatus(header.status),
    notes: header.notes ?? '',
    created_by: header.created_by ?? '',
    created_at: header.created_at,
    updated_at: header.updated_at,
    ordered_qty: ordered,
    delivered_qty: delivered,
    outstanding_qty: Math.max(0, ordered - delivered),
    line_count: lines.length,
    po_attachments: parsePoAttachments(header.po_attachments),
    lines,
  };
}

export async function uploadBlanketPoAttachment(
  blanketId: string,
  file: File,
): Promise<DemBlanketAttachmentRef> {
  const db = demDb();
  const safeName = file.name.replace(/[^\w.\-()+ ]+/g, '_').slice(0, 120) || 'po';
  const path = `${blanketId}/po/${Date.now()}_${safeName}`;
  const { error } = await db.storage.from(BLANKET_ATTACHMENT_BUCKET).upload(path, file, {
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw new Error(error.message || 'Failed to upload PO attachment.');
  return {
    path,
    fileName: file.name,
    size: file.size,
    mime: file.type || undefined,
  };
}

export async function getBlanketPoAttachmentSignedUrl(path: string): Promise<string> {
  const db = demDb();
  const { data, error } = await db.storage
    .from(BLANKET_ATTACHMENT_BUCKET)
    .createSignedUrl(path, 60 * 30);
  if (error) throw new Error(error.message || 'Failed to open attachment.');
  if (!data?.signedUrl) throw new Error('Failed to open attachment.');
  return data.signedUrl;
}

export async function setBlanketPoAttachments(
  blanketId: string,
  attachments: DemBlanketAttachmentRef[],
): Promise<void> {
  const db = demDb();
  const { error } = await db
    .from('blanket_orders')
    .update({
      po_attachments: attachments,
      updated_at: new Date().toISOString(),
    })
    .eq('id', blanketId);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function removeBlanketPoAttachmentFile(path: string): Promise<void> {
  const trimmed = path.trim();
  if (!trimmed) return;
  const db = demDb();
  const { error } = await db.storage.from(BLANKET_ATTACHMENT_BUCKET).remove([trimmed]);
  if (error) throw new Error(error.message || 'Failed to remove attachment.');
}

async function syncBlanketCompletionStatus(
  blanketId: string,
  currentStatus: DemBlanketOrderStatus,
  lines: DemBlanketOrderLine[],
): Promise<DemBlanketOrderStatus> {
  const fully = isBlanketFullyDelivered(lines);
  let next: DemBlanketOrderStatus | null = null;
  if (fully && currentStatus !== 'Completed') {
    next = 'Completed';
  } else if (!fully && currentStatus === 'Completed') {
    next = 'Open';
  }
  if (!next) return currentStatus;
  await setBlanketOrderStatus(blanketId, next);
  return next;
}

export async function listBlanketOrders(opts?: {
  status?: DemBlanketOrderStatus | 'All';
  search?: string;
}): Promise<DemBlanketOrder[]> {
  const db = demDb();
  let query = db
    .from('blanket_orders')
    .select(
      BLANKET_HEADER_SELECT,
    )
    .order('created_at', { ascending: false });

  const status = opts?.status ?? 'All';
  if (status === 'Open' || status === 'Closed' || status === 'Completed') {
    query = query.eq('status', status);
  }

  const search = opts?.search?.trim();
  if (search) {
    query = query.or(
      `po_number.ilike.%${search}%,customer_code.ilike.%${search}%,customer_name.ilike.%${search}%`,
    );
  }

  const { data, error } = await query;
  if (error) throw new Error(formatSupabaseError(error));

  const headers = (data ?? []) as Array<HeaderRow & { lines?: LineRow[] | null }>;
  const blanketIds = headers.map((h) => h.id);
  const movementsByBlanket = new Map<string, MovementRow[]>();

  if (blanketIds.length > 0) {
    const lineIds = headers.flatMap((h) => (h.lines ?? []).map((l) => l.id));
    if (lineIds.length > 0) {
      const { data: moves, error: moveError } = await db
        .from('blanket_order_movements')
        .select('id, blanket_line_id, dem_order_id, dem_batch_id, qty, source, created_by, created_at')
        .in('blanket_line_id', lineIds);
      if (moveError) throw new Error(formatSupabaseError(moveError));

      const lineToBlanket = new Map<string, string>();
      for (const h of headers) {
        for (const line of h.lines ?? []) {
          lineToBlanket.set(line.id, h.id);
        }
      }
      for (const move of (moves ?? []) as MovementRow[]) {
        const blanketId = lineToBlanket.get(move.blanket_line_id);
        if (!blanketId) continue;
        const list = movementsByBlanket.get(blanketId) ?? [];
        list.push(move);
        movementsByBlanket.set(blanketId, list);
      }
    }
  }

  const orders = headers.map((header) => {
    const lines = buildLinesWithTotals(header.lines ?? [], movementsByBlanket.get(header.id) ?? []);
    return headerWithTotals(header, lines);
  });

  // Persist Completed when outstanding is fully cleared (and reopen if no longer).
  await Promise.all(
    orders.map(async (order, index) => {
      const next = await syncBlanketCompletionStatus(
        order.id,
        order.status,
        order.lines ?? [],
      );
      if (next !== order.status) {
        orders[index] = { ...order, status: next };
      }
    }),
  );

  return orders;
}

export async function fetchBlanketOrder(id: string): Promise<DemBlanketOrder | null> {
  const db = demDb();
  const { data, error } = await db
    .from('blanket_orders')
    .select(
      BLANKET_HEADER_SELECT,
    )
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) return null;

  const header = data as HeaderRow & { lines?: LineRow[] | null };
  const lineIds = (header.lines ?? []).map((l) => l.id);
  let movements: MovementRow[] = [];
  if (lineIds.length > 0) {
    const { data: moves, error: moveError } = await db
      .from('blanket_order_movements')
      .select('id, blanket_line_id, dem_order_id, dem_batch_id, qty, source, created_by, created_at')
      .in('blanket_line_id', lineIds)
      .order('created_at', { ascending: false });
    if (moveError) throw new Error(formatSupabaseError(moveError));
    movements = (moves ?? []) as MovementRow[];
  }

  const lines = buildLinesWithTotals(header.lines ?? [], movements);
  const lineById = new Map(lines.map((l) => [l.id, l]));
  const movementRows: DemBlanketOrderMovement[] = movements.map((m) => {
    const line = lineById.get(m.blanket_line_id);
    return {
      id: m.id,
      blanket_line_id: m.blanket_line_id,
      dem_order_id: m.dem_order_id,
      dem_batch_id: m.dem_batch_id ?? '',
      qty: num(m.qty),
      source: m.source === 'adjustment' ? 'adjustment' : 'export',
      created_by: m.created_by ?? '',
      created_at: m.created_at,
      item_code: line?.item_code,
      description: line?.description,
    };
  });

  const order = {
    ...headerWithTotals(header, lines),
    movements: movementRows,
  };
  const nextStatus = await syncBlanketCompletionStatus(
    order.id,
    order.status,
    order.lines ?? [],
  );
  return nextStatus === order.status ? order : { ...order, status: nextStatus };
}

export async function createBlanketOrder(input: {
  customer_code: string;
  customer_name: string;
  po_number: string;
  notes?: string;
  created_by: string;
  lines: DemBlanketOrderLineInput[];
}): Promise<DemBlanketOrder> {
  const po = normalizeBlanketPoNumber(input.po_number);
  if (!po) throw new Error('PO number is required.');
  if (!input.customer_code.trim()) throw new Error('Customer is required.');

  const lines = input.lines
    .map((line) => ({
      item_code: line.item_code.trim().toUpperCase(),
      description: line.description.trim(),
      ordered_qty: num(line.ordered_qty),
      uom: line.uom.trim() || 'PCS',
      unit_price: line.unit_price == null ? null : num(line.unit_price),
    }))
    .filter((line) => line.item_code && line.ordered_qty > 0);

  if (lines.length === 0) {
    throw new Error('Add at least one line with item code and quantity.');
  }

  const db = demDb();
  const { data: header, error } = await db
    .from('blanket_orders')
    .insert({
      customer_code: input.customer_code.trim(),
      customer_name: input.customer_name.trim(),
      po_number: po,
      notes: input.notes?.trim() ?? '',
      created_by: input.created_by.trim(),
      status: 'Open',
    })
    .select('id')
    .single();
  if (error) {
    const msg = formatSupabaseError(error);
    if (/duplicate|23505/i.test(msg)) {
      throw new Error(`Blanket PO "${po}" already exists.`);
    }
    throw new Error(msg);
  }

  const blanketId = String((header as { id: string }).id);
  const { error: lineError } = await db.from('blanket_order_lines').insert(
    lines.map((line) => ({
      blanket_id: blanketId,
      item_code: line.item_code,
      description: line.description,
      ordered_qty: line.ordered_qty,
      uom: line.uom,
      unit_price: line.unit_price,
    })),
  );
  if (lineError) {
    await db.from('blanket_orders').delete().eq('id', blanketId);
    throw new Error(formatSupabaseError(lineError));
  }

  const created = await fetchBlanketOrder(blanketId);
  if (!created) throw new Error('Blanket order created but could not be reloaded.');
  return created;
}

export async function setBlanketOrderStatus(
  id: string,
  status: DemBlanketOrderStatus,
): Promise<void> {
  const db = demDb();
  const { error } = await db
    .from('blanket_orders')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function updateBlanketOrder(input: {
  id: string;
  customer_code: string;
  customer_name: string;
  po_number: string;
  notes?: string;
  lines: Array<DemBlanketOrderLineInput & { id?: string }>;
}): Promise<DemBlanketOrder> {
  const blanketId = input.id.trim();
  if (!blanketId) throw new Error('Blanket order id is required.');

  const po = normalizeBlanketPoNumber(input.po_number);
  if (!po) throw new Error('PO number is required.');
  if (!input.customer_code.trim()) throw new Error('Customer is required.');

  const current = await fetchBlanketOrder(blanketId);
  if (!current) throw new Error('Blanket order not found.');

  const existingById = new Map((current.lines ?? []).map((line) => [line.id, line]));
  const nextLines = input.lines
    .map((line) => ({
      id: line.id?.trim() || undefined,
      item_code: line.item_code.trim().toUpperCase(),
      description: line.description.trim(),
      ordered_qty: num(line.ordered_qty),
      uom: line.uom.trim() || 'PCS',
      unit_price: line.unit_price == null ? null : num(line.unit_price),
    }))
    .filter((line) => line.item_code && line.ordered_qty > 0);

  if (nextLines.length === 0) {
    throw new Error('Add at least one line with item code and quantity.');
  }

  const seenCodes = new Set<string>();
  for (const line of nextLines) {
    if (seenCodes.has(line.item_code)) {
      throw new Error(`Duplicate item ${line.item_code}. Each item can only appear once.`);
    }
    seenCodes.add(line.item_code);

    if (!line.id) continue;
    const existing = existingById.get(line.id);
    if (!existing) {
      throw new Error('One of the lines no longer exists. Refresh and try again.');
    }
    if (existing.delivered_qty > 0 && existing.item_code !== line.item_code) {
      throw new Error(
        `Cannot change item ${existing.item_code}: it already has deliveries.`,
      );
    }
    if (line.ordered_qty + 1e-9 < existing.delivered_qty) {
      throw new Error(
        `Ordered qty for ${line.item_code} cannot be below delivered (${existing.delivered_qty}).`,
      );
    }
  }

  const keptIds = new Set(nextLines.map((line) => line.id).filter(Boolean) as string[]);
  const toDelete: string[] = [];
  for (const existing of current.lines ?? []) {
    if (keptIds.has(existing.id)) continue;
    if (existing.delivered_qty > 0) {
      throw new Error(
        `Cannot remove ${existing.item_code}: it already has deliveries.`,
      );
    }
    toDelete.push(existing.id);
  }

  const db = demDb();
  const { error: headerError } = await db
    .from('blanket_orders')
    .update({
      customer_code: input.customer_code.trim(),
      customer_name: input.customer_name.trim(),
      po_number: po,
      notes: input.notes?.trim() ?? '',
      updated_at: new Date().toISOString(),
    })
    .eq('id', blanketId);
  if (headerError) {
    const msg = formatSupabaseError(headerError);
    if (/duplicate|23505/i.test(msg)) {
      throw new Error(`Blanket PO "${po}" already exists.`);
    }
    throw new Error(msg);
  }

  if (toDelete.length > 0) {
    const { error: deleteError } = await db
      .from('blanket_order_lines')
      .delete()
      .in('id', toDelete);
    if (deleteError) throw new Error(formatSupabaseError(deleteError));
  }

  const toInsert = nextLines.filter((line) => !line.id);
  const toUpdate = nextLines.filter((line) => Boolean(line.id));

  for (const line of toUpdate) {
    const { error: updateError } = await db
      .from('blanket_order_lines')
      .update({
        item_code: line.item_code,
        description: line.description,
        ordered_qty: line.ordered_qty,
        uom: line.uom,
        unit_price: line.unit_price,
      })
      .eq('id', line.id!)
      .eq('blanket_id', blanketId);
    if (updateError) throw new Error(formatSupabaseError(updateError));
  }

  if (toInsert.length > 0) {
    const { error: insertError } = await db.from('blanket_order_lines').insert(
      toInsert.map((line) => ({
        blanket_id: blanketId,
        item_code: line.item_code,
        description: line.description,
        ordered_qty: line.ordered_qty,
        uom: line.uom,
        unit_price: line.unit_price,
      })),
    );
    if (insertError) throw new Error(formatSupabaseError(insertError));
  }

  const updated = await fetchBlanketOrder(blanketId);
  if (!updated) throw new Error('Blanket order updated but could not be reloaded.');
  const nextStatus = await syncBlanketCompletionStatus(
    updated.id,
    updated.status,
    updated.lines ?? [],
  );
  return nextStatus === updated.status ? updated : { ...updated, status: nextStatus };
}

/**
 * After DEM export: match open blanket by PO (ref_no) and deduct line qtys.
 * Idempotent per (order_id, line, batch). Missing blanket → no-op.
 */
export async function applyBlanketDeductionsForExport(input: {
  orderIds: string[];
  batchIds: string[];
  exportedBy: string;
}): Promise<void> {
  const orderIds = [...new Set(input.orderIds.map((id) => id.trim()).filter(Boolean))];
  if (orderIds.length === 0) return;

  const db = demDb();
  const { data: orders, error: orderError } = await db
    .from('orders')
    .select('order_id, ref_no, items:order_items(item_code, description, qty, batch_id)')
    .in('order_id', orderIds);
  if (orderError) throw new Error(formatSupabaseError(orderError));
  if (!orders?.length) return;

  const batchIdSet = new Set(input.batchIds.map((id) => id.trim()).filter(Boolean));
  const useBatchFilter = batchIdSet.size > 0;

  const poNumbers = [
    ...new Set(
      (orders as Array<{ ref_no?: string }>)
        .map((o) => normalizeBlanketPoNumber(String(o.ref_no ?? '')))
        .filter(Boolean),
    ),
  ];
  if (poNumbers.length === 0) return;

  const { data: blankets, error: blanketError } = await db
    .from('blanket_orders')
    .select(
      'id, po_number, status, lines:blanket_order_lines(id, item_code, ordered_qty)',
    )
    .eq('status', 'Open')
    .in('po_number', poNumbers);
  if (blanketError) throw new Error(formatSupabaseError(blanketError));
  if (!blankets?.length) return;

  const blanketByPo = new Map<
    string,
    {
      id: string;
      lines: Map<string, string>;
      /** Remaining outstanding per blanket_line_id (clamped, depleted as we insert). */
      remainingByLineId: Map<string, number>;
    }
  >();

  // Preload movements so export never pushes outstanding below zero.
  const allLineIds = (
    blankets as Array<{ lines?: Array<{ id: string }> | null }>
  ).flatMap((b) => (b.lines ?? []).map((l) => l.id));
  const deliveredByLineId = new Map<string, number>();
  if (allLineIds.length > 0) {
    const { data: moves, error: moveError } = await db
      .from('blanket_order_movements')
      .select('blanket_line_id, qty')
      .in('blanket_line_id', allLineIds);
    if (moveError) throw new Error(formatSupabaseError(moveError));
    for (const move of (moves ?? []) as Array<{ blanket_line_id?: string; qty?: number | string }>) {
      const id = String(move.blanket_line_id ?? '');
      if (!id) continue;
      deliveredByLineId.set(id, (deliveredByLineId.get(id) ?? 0) + num(move.qty));
    }
  }

  for (const blanket of blankets as Array<{
    id: string;
    po_number: string;
    lines?: Array<{ id: string; item_code: string; ordered_qty?: number | string }> | null;
  }>) {
    const lineMap = new Map<string, string>();
    const remainingByLineId = new Map<string, number>();
    for (const line of blanket.lines ?? []) {
      const code = String(line.item_code ?? '')
        .trim()
        .toUpperCase();
      if (!code) continue;
      lineMap.set(code, line.id);
      const ordered = num(line.ordered_qty);
      const delivered = deliveredByLineId.get(line.id) ?? 0;
      remainingByLineId.set(line.id, Math.max(0, ordered - delivered));
    }
    blanketByPo.set(normalizeBlanketPoNumber(blanket.po_number), {
      id: blanket.id,
      lines: lineMap,
      remainingByLineId,
    });
  }

  type MoveInsert = {
    blanket_line_id: string;
    dem_order_id: string;
    dem_batch_id: string;
    qty: number;
    source: 'export';
    created_by: string;
  };
  const inserts: MoveInsert[] = [];

  for (const order of orders as Array<{
    order_id: string;
    ref_no?: string;
    items?: Array<{
      item_code?: string;
      description?: string;
      qty?: number | string;
      batch_id?: string | null;
    }> | null;
  }>) {
    const po = normalizeBlanketPoNumber(String(order.ref_no ?? ''));
    const blanket = blanketByPo.get(po);
    if (!blanket) continue;

    // Sum qty per item_code (+ batch key) for this export wave.
    const qtyByKey = new Map<string, { itemCode: string; batchKey: string; qty: number }>();
    for (const item of order.items ?? []) {
      const itemCode = String(item.item_code ?? '')
        .trim()
        .toUpperCase();
      const description = String(item.description ?? '');
      if (!itemCode || isDeliveryFeeLine(itemCode, description)) continue;

      const batchId = String(item.batch_id ?? '').trim();
      if (useBatchFilter) {
        if (!batchId || !batchIdSet.has(batchId)) continue;
      }

      const batchKey = useBatchFilter ? batchId : '';
      const key = `${batchKey}::${itemCode}`;
      const prev = qtyByKey.get(key);
      const qty = num(item.qty);
      if (qty <= 0) continue;
      if (prev) {
        prev.qty += qty;
      } else {
        qtyByKey.set(key, { itemCode, batchKey, qty });
      }
    }

    for (const { itemCode, batchKey, qty } of qtyByKey.values()) {
      const lineId = blanket.lines.get(itemCode);
      if (!lineId) continue;
      const remaining = blanket.remainingByLineId.get(lineId) ?? 0;
      if (remaining <= 0) continue;
      const deduct = Math.min(qty, remaining);
      if (deduct <= 0) continue;
      blanket.remainingByLineId.set(lineId, Math.max(0, remaining - deduct));
      inserts.push({
        blanket_line_id: lineId,
        dem_order_id: order.order_id,
        dem_batch_id: batchKey,
        qty: deduct,
        source: 'export',
        created_by: input.exportedBy.trim(),
      });
    }
  }

  if (inserts.length === 0) return;

  const { error: insertError } = await db
    .from('blanket_order_movements')
    .upsert(inserts, {
      onConflict: 'dem_order_id,blanket_line_id,dem_batch_id',
      ignoreDuplicates: true,
    });
  if (insertError) throw new Error(formatSupabaseError(insertError));

  const touchedBlanketIds = [
    ...new Set(
      [...blanketByPo.values()]
        .filter((b) => inserts.some((row) => [...b.lines.values()].includes(row.blanket_line_id)))
        .map((b) => b.id),
    ),
  ];
  if (touchedBlanketIds.length > 0) {
    await db
      .from('blanket_orders')
      .update({ updated_at: new Date().toISOString() })
      .in('id', touchedBlanketIds);

    for (const blanketId of touchedBlanketIds) {
      const row = await fetchBlanketOrder(blanketId);
      if (!row) continue;
      await syncBlanketCompletionStatus(row.id, row.status, row.lines ?? []);
    }
  }
}
